import axios from "axios";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { type UniversalLinkData, universalLinkService } from "../services/universalLink.service";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { ClientType } from "../utils/models";
import axiosInstance from "../utils/networkWrapper";

export const useUniversalLink = () => {
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();

	const handleSimpleLink = useCallback(
		(data: UniversalLinkData) => {
			const { path, params } = data;
			const searchParams = new URLSearchParams(params);
			const queryString = searchParams.toString();
			const suffix = queryString ? `?${queryString}` : "";

			trackEvent("simple_link_navigate", { path });
			navigate(`${path}${suffix}`);
			return true;
		},
		[navigate],
	);

	const handleUniversalLink = useCallback(
		async (data: UniversalLinkData) => {
			const { params } = data;
			const searchParams = new URLSearchParams(params);

			const linkToken = searchParams.get("link_token");

			// No link_token - handle as a simple direct link
			if (!linkToken) {
				const handled = handleSimpleLink(data);
				if (!handled) {
					trackErrorEvent("universal_link_unrecognized_path", data);
				}
				return;
			}

			try {
				const baseURL = axiosInstance.defaults.baseURL;

				const localAxios = axios.create({
					baseURL: baseURL,
					headers: {
						"client-type": ClientType.MOBILE,
						accept: "application/json",
					},
				});

				const response = await localAxios.get("/link", {
					params: { link_token: linkToken },
				});

				trackEvent("universal link redirect response: ", { response: response });

				// Read redirect_url from JSON response (for mobile) or Location header (for web fallback)
				const redirectUrl = response.data.redirect_url || response.headers.location || response.headers.Location;

				if (redirectUrl) {
					// Parse the redirect URL
					const urlObj = new URL(redirectUrl);
					const frontendPath = urlObj.pathname;
					const frontendParams = new URLSearchParams(urlObj.search);

					// Apply special handling based on path
					if (frontendPath.startsWith("/day-schedule")) {
						const date = frontendParams.get("date") || frontendPath.split("/day-schedule/")[1];
						if (date) {
							navigate(`/${chosenDepartment.department_id}/day-schedule/${date}`);
						} else {
							navigate(`${frontendPath}?${frontendParams.toString()}`);
						}
					} else {
						// For other paths, navigate normally
						navigate(`${frontendPath}?${frontendParams.toString()}`);
					}
				} else {
					trackErrorEvent("No redirect URL in link response", { linkToken });
				}
			} catch (error) {
				trackErrorEvent("Error resolving link token", error, {
					linkToken: linkToken,
					baseURL: axiosInstance.defaults.baseURL,
					errorMessage: error instanceof Error ? error.message : "Unknown error type",
					errorString: String(error),
				});
			}
			return;
		},
		[navigate, chosenDepartment, handleSimpleLink],
	);

	useEffect(() => {
		// Add listener for universal links
		universalLinkService.addListener(handleUniversalLink);

		// Cleanup listener on unmount
		return () => {
			universalLinkService.removeListener(handleUniversalLink);
		};
	}, [handleUniversalLink]);

	return {
		parseUniversalLink: universalLinkService.parseUniversalLink.bind(universalLinkService),
	};
};
